import { useState } from "react"
import { useParams } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { apiTenant, ApiError } from "../lib/api"

interface LeaveType {
  id: string
  code: string
  name: string
  paid: boolean
}

interface LeaveRequest {
  id: string
  leaveTypeId: string
  fromDate: string
  toDate: string
  days: string
  reason: string | null
  status: "pending" | "approved" | "rejected" | "cancelled" | "consumed"
  decisionNote: string | null
  decidedAt: string | null
  leaveType: { code: string; name: string; paid: boolean }
}

interface LeaveBalance {
  leaveTypeId: string
  entitledDays: string
  usedDays: string
  pendingDays: string
  carriedOverDays: string
  leaveType: { code: string; name: string; paid: boolean }
}

const STATUS_PILL: Record<LeaveRequest["status"], string> = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
  cancelled: "bg-slate-200 text-slate-600",
  consumed: "bg-blue-100 text-blue-700",
}

export function MyLeave() {
  const { slug = "" } = useParams<{ slug: string }>()
  const qc = useQueryClient()
  const [showSubmit, setShowSubmit] = useState(false)

  const requests = useQuery({
    queryKey: ["my-leave-requests", slug],
    enabled: !!slug,
    queryFn: () => apiTenant<{ requests: LeaveRequest[] }>(slug, "/me/leave/requests"),
  })

  const balances = useQuery({
    queryKey: ["my-leave-balances", slug],
    enabled: !!slug,
    queryFn: () =>
      apiTenant<{ balances: LeaveBalance[] }>(slug, `/me/leave/balances?year=${new Date().getFullYear()}`),
  })

  const cancel = useMutation({
    mutationFn: (id: string) => apiTenant(slug, `/me/leave/requests/${id}/cancel`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-leave-requests", slug] })
      qc.invalidateQueries({ queryKey: ["my-leave-balances", slug] })
    },
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">My leave</h1>
        <button
          onClick={() => setShowSubmit(true)}
          className="px-3 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded"
        >
          + Submit request
        </button>
      </div>

      {/* Balances */}
      <div className="bg-white border border-slate-200 rounded p-4">
        <div className="text-sm font-medium text-slate-700 mb-2">Balances ({new Date().getFullYear()})</div>
        {(balances.data?.balances ?? []).length === 0 ? (
          <div className="text-sm text-slate-400">No balances yet — they're created on your first request.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {balances.data!.balances.map((b) => {
              const remaining = Number(b.entitledDays) - Number(b.usedDays)
              return (
                <div key={b.leaveTypeId} className="bg-slate-50 border border-slate-200 rounded p-2">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">{b.leaveType.code}</div>
                  <div className="text-base font-mono font-semibold">{remaining.toFixed(1)}</div>
                  <div className="text-[11px] text-slate-500">
                    {Number(b.usedDays).toFixed(1)} used
                    {Number(b.pendingDays) > 0 && <> · {Number(b.pendingDays).toFixed(1)} pending</>}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Requests */}
      <div className="bg-white border border-slate-200 rounded">
        <div className="px-4 py-3 border-b border-slate-200 font-medium">My requests</div>
        {requests.isLoading && <div className="p-6 text-sm text-slate-400">Loading…</div>}
        {requests.data && requests.data.requests.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-400">No requests yet.</div>
        )}
        {requests.data && requests.data.requests.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Type</th>
                <th className="text-left px-4 py-2 font-medium">Dates</th>
                <th className="text-right px-4 py-2 font-medium">Days</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-left px-4 py-2 font-medium">Reason</th>
                <th className="text-right px-4 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {requests.data.requests.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 last:border-b-0">
                  <td className="px-4 py-2 font-mono text-xs">{r.leaveType.code}</td>
                  <td className="px-4 py-2 font-mono text-xs">
                    {r.fromDate.slice(0, 10)} → {r.toDate.slice(0, 10)}
                  </td>
                  <td className="px-4 py-2 text-right font-mono">{Number(r.days).toFixed(1)}</td>
                  <td className="px-4 py-2">
                    <span className={`inline-block px-2 py-0.5 text-xs rounded ${STATUS_PILL[r.status]}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-600 max-w-[200px] truncate" title={r.reason ?? ""}>
                    {r.reason ?? "—"}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {(r.status === "pending" || r.status === "approved") && (
                      <button
                        onClick={() => {
                          if (window.confirm("Cancel this request? Days will refund to your balance.")) cancel.mutate(r.id)
                        }}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showSubmit && (
        <SubmitMyRequest
          slug={slug}
          onClose={() => setShowSubmit(false)}
          onSubmitted={() => {
            qc.invalidateQueries({ queryKey: ["my-leave-requests", slug] })
            qc.invalidateQueries({ queryKey: ["my-leave-balances", slug] })
            setShowSubmit(false)
          }}
        />
      )}
    </div>
  )
}

function SubmitMyRequest(props: { slug: string; onClose: () => void; onSubmitted: () => void }) {
  const [leaveTypeId, setLeaveTypeId] = useState("")
  const [fromDate, setFromDate] = useState(new Date().toISOString().slice(0, 10))
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10))
  const [reason, setReason] = useState("")
  const [error, setError] = useState("")

  const types = useQuery({
    queryKey: ["leave-types-public", props.slug],
    queryFn: () => apiTenant<{ leaveTypes: LeaveType[] }>(props.slug, "/me/leave-types"),
  })

  const submit = useMutation({
    mutationFn: () =>
      apiTenant(props.slug, "/me/leave/requests", {
        method: "POST",
        body: JSON.stringify({ leaveTypeId, fromDate, toDate, reason: reason.trim() || null }),
      }),
    onSuccess: props.onSubmitted,
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.message === "to_before_from") setError("End date is before start date.")
        else if (err.message === "zero_day_request") setError("Request must cover at least one day.")
        else setError(err.message)
      } else setError(err instanceof Error ? err.message : "Failed")
    },
  })

  function days() {
    if (!fromDate || !toDate) return 0
    const a = new Date(fromDate),
      b = new Date(toDate)
    if (b < a) return 0
    return Math.floor((b.getTime() - a.getTime()) / 86_400_000) + 1
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError("")
    if (!leaveTypeId) {
      setError("Pick a leave type.")
      return
    }
    submit.mutate()
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <form onSubmit={handleSubmit} className="bg-white rounded-lg w-full max-w-md shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="font-semibold">Submit leave request</div>
          <button type="button" onClick={props.onClose} className="text-slate-400 hover:text-slate-700">
            ×
          </button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Type *">
            <select
              value={leaveTypeId}
              onChange={(e) => setLeaveTypeId(e.target.value)}
              className="form-input"
            >
              <option value="">—</option>
              {types.data?.leaveTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.code} ({t.paid ? "paid" : "unpaid"})
                </option>
              ))}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="From *">
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="form-input" />
            </Field>
            <Field label="To *">
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="form-input" />
            </Field>
          </div>
          <div className="text-xs text-slate-500">
            Days: <strong>{days()}</strong>
          </div>
          <Field label="Reason">
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="form-input"
              placeholder="Optional context"
            />
          </Field>
          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button type="button" onClick={props.onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900">
            Cancel
          </button>
          <button
            type="submit"
            disabled={submit.isPending}
            className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white text-sm font-medium rounded"
          >
            {submit.isPending ? "Submitting…" : "Submit"}
          </button>
        </div>
      </form>
    </div>
  )
}

function Field(props: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-slate-600 mb-1">{props.label}</div>
      {props.children}
    </label>
  )
}
